/**
 * Approval state for one planned meal.
 *
 * Lives in a .ts rather than beside the cards for two reasons: tsconfig emits
 * `jsx: preserve`, so Jest can never import from a .tsx; and keeping the
 * branching here means the three cards that render thumbs stay thin and
 * cannot drift apart in behaviour.
 */
import { isEntryMeal } from '../../redux/mealEntry';
import type { PlannedMeal } from '../../redux/mealPlanSlice';

export type ApprovalState = 'hidden' | 'pending' | 'mine' | 'agreed' | 'refused';

export interface ApprovalInput {
  meal: PlannedMeal | null | undefined;
  myUid: string | null;
  /** The other household member. Null while unpaired. */
  partnerUid: string | null;
  householdId: string | null;
  /** Today as YYYY-MM-DD, injected so this stays pure and testable. */
  today: string;
}

/**
 * An opinion counts only while the slot still holds the recipe it was cast
 * on — the read-time guard that makes stale opinions harmless.
 */
const liveVote = (meal: PlannedMeal, uid: string | null): 'yes' | 'no' | null => {
  if (!uid) return null;
  const a = meal.approvals?.[uid];
  if (!a) return null;
  return a.onRecipeId === meal.recipeId ? a.value : null;
};

/** Nothing to have an opinion about in "leftovers" or "pizza". */
const isVotable = (meal: PlannedMeal): boolean =>
  !isEntryMeal(meal) && !meal.freeText && meal.recipeId !== 'entry';

export const getMyVote = ({ meal, myUid }: ApprovalInput) =>
  meal ? liveVote(meal, myUid) : null;

export const getPartnerVote = ({ meal, partnerUid }: ApprovalInput) =>
  meal ? liveVote(meal, partnerUid) : null;

export const getApprovalState = (input: ApprovalInput): ApprovalState => {
  const { meal, householdId, partnerUid, today } = input;

  if (!meal) return 'hidden';
  // Solo users see exactly today's card — no thumbs, and no nudge to pair.
  if (!householdId || !partnerUid) return 'hidden';
  if (!isVotable(meal)) return 'hidden';
  // Nothing to decide about a meal that already happened.
  if (meal.status === 'cooked') return 'hidden';
  if (meal.scheduledFor < today) return 'hidden';

  const mine = getMyVote(input);
  const theirs = getPartnerVote(input);

  if (mine === 'no' || theirs === 'no') return 'refused';
  if (mine === 'yes' && theirs === 'yes') return 'agreed';
  if (mine) return 'mine';
  return 'pending';
};
